import { z } from 'zod';
import type {
  BaseParameterDetailAddition,
  BaseSessionDetailAddition,
  BaseSqlDetailAddition,
} from '../types';
import { nullishAsUndefined, nullishToEmpty, zodSafeParseNumber } from './utils';

export const sessionDetailSchema = z
  .object({
    sid: z.number({ required_error: 'SID', invalid_type_error: 'SID' }),
    serial: nullishAsUndefined(z.number().optional()),
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
    isOpenedFromPA: nullishAsUndefined(z.boolean().optional()),
    init: nullishAsUndefined(z.boolean().optional()),
    childNumber: zodSafeParseNumber,
    sqlChildNumber: zodSafeParseNumber,
    planHashValue: zodSafeParseNumber,
    hashValue: zodSafeParseNumber,
    collectTime: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ childNumber, sqlChildNumber, ...rest }) => ({
    ...rest,
    childNumber: childNumber ?? sqlChildNumber,
  }));

export const sqlDetailSchema = z
  .object({
    sqlId: z.string({ required_error: 'SQL ID' }).min(1, 'SQL ID'),
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
    module: nullishAsUndefined(z.string().optional()),
    parsingSchemaName: nullishAsUndefined(z.string().optional()),
    schemaName: nullishAsUndefined(z.string().optional()),
    schema: nullishAsUndefined(z.string().optional()),
    planHashValue: zodSafeParseNumber,
    hashValue: zodSafeParseNumber,
    childNumber: zodSafeParseNumber,
    sqlChildNumber: zodSafeParseNumber,
    collectTime: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ parsingSchemaName, schemaName, schema, childNumber, sqlChildNumber, ...rest }) => ({
    ...rest,
    parsingSchemaName: parsingSchemaName ?? schemaName ?? schema,
    childNumber: childNumber ?? sqlChildNumber,
  }));

export const parameterDetailSchema = z
  .object({
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    name: nullishAsUndefined(z.string().optional()),
    parameterName: nullishAsUndefined(z.string().optional()),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ name, parameterName, ...rest }) => ({
    ...rest,
    parameterName: name ?? parameterName,
  }))
  .pipe(
    z.object({
      parameterName: z.string({ required_error: 'Parameter Name' }).min(1, 'Parameter Name'),
      instanceId: z.string().min(1, 'Instance ID'),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export const documentTextSchema = z.object({
  sqlId: z.preprocess(nullishToEmpty(''), z.string({ required_error: 'SQL ID' }).min(1, 'SQL ID')),
  instanceId: z.preprocess(
    nullishToEmpty(''),
    z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  ),
});

export type SessionDetailSchema = z.infer<typeof sessionDetailSchema>;
export type SqlDetailSchema = z.infer<typeof sqlDetailSchema>;
export type ParameterDetailSchema = z.infer<typeof parameterDetailSchema>;
export type DocumentTextSchema = z.infer<typeof documentTextSchema>;

export type TiberoSessionDetailAddition = BaseSessionDetailAddition & SessionDetailSchema;
export type TiberoSqlDetailAddition = BaseSqlDetailAddition & SqlDetailSchema;
export type TiberoParameterDetailAddition = Omit<
  BaseParameterDetailAddition & ParameterDetailSchema,
  'parameterName'
>;
